import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { SuggestionChipState } from '@/types';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { shimmerTextClassName, useShimmerMotion } from '@/components/ui/use-shimmer-motion';
import { getResolvedSuggestionPillClassName } from '@/components/chat/chat-message-styles';
import { cn } from '@/lib/utils';

const CHIP_TOOLTIP_CLASS =
  'max-w-[min(100vw-2rem,20rem)] rounded-lg border border-border bg-popover px-3 py-2 text-popover-foreground shadow-md';

const SUGGESTION_LABEL_REVEAL_MS = 450;

interface ChatSuggestionsInThreadProps {
  chips: SuggestionChipState[];
  maxPills?: number;
  disabled?: boolean;
  onSendSuggestion: (message: string) => void;
  className?: string;
}

export function ChatSuggestionsInThread({
  chips,
  maxPills = 4,
  disabled = false,
  onSendSuggestion,
  className,
}: ChatSuggestionsInThreadProps) {
  const slots = chips.slice(0, maxPills);
  const reduceMotion = Boolean(useReducedMotion());
  /** Slots that have reached ready at least once — never revert to the loader. */
  const readyLatchRef = React.useRef<Set<number>>(new Set());
  /** Slots that finished the loader→pill opacity transition — skip re-fading. */
  const enteredReadyRef = React.useRef<Set<number>>(new Set());
  if (!slots.length) return null;

  return (
    <TooltipProvider delayDuration={250}>
      <div className={cn('flex w-full flex-wrap items-center gap-2', className)}>
        {slots.map((chip, index) => {
          const label = chip.label || '';
          const isReady = Boolean(label && chip.message?.trim() && chip.messageComplete);
          if (isReady) readyLatchRef.current.add(index);
          const showReady = isReady || readyLatchRef.current.has(index);
          const showSkeleton = !showReady;
          const canClick = !disabled && chip.clickable && Boolean(chip.message?.trim());

          const chipButton = (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!canClick}
              aria-label={chip.message ? `Send suggestion: ${chip.message}` : 'Loading suggestion'}
              className={cn(
                getResolvedSuggestionPillClassName(),
                'disabled:cursor-default disabled:opacity-70'
              )}
              onClick={() => canClick && onSendSuggestion(chip.message)}
            >
              <AnimatedSuggestionLabel
                key={`label-${index}`}
                label={label}
                reduceMotion={reduceMotion}
              />
            </Button>
          );

          return (
            <div key={`slot-${index}`} className="inline-flex max-w-full">
              <AnimatePresence mode="wait" initial={false}>
                {showSkeleton ? (
                  <SuggestionLoadingChip key="loading" reduceMotion={reduceMotion} />
                ) : (
                  <motion.div
                    key="ready"
                    className="inline-flex max-w-full"
                    initial={
                      reduceMotion || enteredReadyRef.current.has(index)
                        ? false
                        : { opacity: 0 }
                    }
                    animate={{ opacity: 1 }}
                    exit={reduceMotion ? undefined : { opacity: 0 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                    onAnimationComplete={() => {
                      enteredReadyRef.current.add(index);
                    }}
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>{chipButton}</TooltipTrigger>
                      <TooltipContent
                        side="top"
                        align="start"
                        sideOffset={6}
                        hideArrow
                        className={CHIP_TOOLTIP_CLASS}
                      >
                        <p className="text-xs leading-snug text-foreground">{chip.message}</p>
                      </TooltipContent>
                    </Tooltip>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

function SuggestionLoadingChip({ reduceMotion }: { reduceMotion: boolean }) {
  return (
    <motion.div
      data-slot="suggestion-loading-chip"
      aria-hidden="true"
      className="flex h-7 w-7 items-center justify-center rounded-full bg-muted/80 text-muted-foreground/70"
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={reduceMotion ? undefined : { opacity: 0 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
    >
      <motion.span
        data-slot="suggestion-loading-spinner"
        className="h-3 w-3 rounded-full border border-muted-foreground/25 border-t-muted-foreground/80"
        animate={reduceMotion ? undefined : { rotate: 360 }}
        transition={reduceMotion ? undefined : { duration: 1, ease: 'linear', repeat: Infinity }}
      />
    </motion.div>
  );
}

function AnimatedSuggestionLabel({
  label,
  reduceMotion,
}: {
  label: string;
  reduceMotion: boolean;
}) {
  const chars = React.useMemo(() => Array.from(label), [label]);
  const skipMotion = reduceMotion || typeof window === 'undefined';
  const hasRevealedRef = React.useRef(skipMotion);
  const prevLabelRef = React.useRef('');
  const [settled, setSettled] = React.useState(skipMotion);
  const [visibleCount, setVisibleCount] = React.useState(skipMotion ? chars.length : 0);
  const { style, initial, animate } = useShimmerMotion({
    textLength: label.length,
    duration: SUGGESTION_LABEL_REVEAL_MS / 1000,
    spread: 2,
  });

  React.useEffect(() => {
    if (skipMotion || hasRevealedRef.current) {
      setVisibleCount(chars.length);
      setSettled(true);
      prevLabelRef.current = label;
      return;
    }

    const previousLabel = prevLabelRef.current;
    if (
      previousLabel &&
      label !== previousLabel &&
      label.length >= previousLabel.length &&
      label.startsWith(previousLabel)
    ) {
      hasRevealedRef.current = true;
      setVisibleCount(chars.length);
      setSettled(true);
      prevLabelRef.current = label;
      return;
    }

    prevLabelRef.current = label;
    setSettled(false);
    setVisibleCount(0);

    if (!chars.length) {
      setSettled(true);
      return;
    }

    const stepMs = Math.max(16, SUGGESTION_LABEL_REVEAL_MS / chars.length);
    let nextCount = 0;
    const revealTimer = window.setInterval(() => {
      nextCount += 1;
      setVisibleCount(Math.min(nextCount, chars.length));
      if (nextCount >= chars.length) {
        window.clearInterval(revealTimer);
      }
    }, stepMs);
    const settleTimer = window.setTimeout(() => {
      setVisibleCount(chars.length);
      setSettled(true);
      hasRevealedRef.current = true;
    }, SUGGESTION_LABEL_REVEAL_MS + 80);

    return () => {
      window.clearInterval(revealTimer);
      window.clearTimeout(settleTimer);
    };
  }, [chars.length, label, skipMotion]);

  if (skipMotion || settled) {
    return <span className="truncate">{label}</span>;
  }

  const visibleChars = chars.slice(0, visibleCount);

  return (
    <motion.span
      aria-label={label}
      className={cn(
        shimmerTextClassName,
        'inline-flex max-w-full overflow-hidden whitespace-nowrap leading-normal',
      )}
      style={style}
      initial={initial}
      animate={animate}
      transition={{ duration: SUGGESTION_LABEL_REVEAL_MS / 1000, ease: 'easeOut' }}
      onAnimationComplete={() => {
        hasRevealedRef.current = true;
        setSettled(true);
      }}
    >
      {visibleChars.map((char, index) => (
        <motion.span
          key={`${char}-${index}`}
          aria-hidden="true"
          initial={{ opacity: 0, y: 3 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.24,
            ease: 'easeOut',
          }}
        >
          {char === ' ' ? '\u00a0' : char}
        </motion.span>
      ))}
    </motion.span>
  );
}

export function emptySuggestionSlots(count: number): SuggestionChipState[] {
  return Array.from({ length: count }, () => ({
    label: '',
    message: '',
    clickable: false,
  }));
}
